import React, { useRef } from 'react';
import {
  Context,
  createContext,
  useContextSelector,
} from 'use-context-selector';
import { Signal } from '@preact/signals-react';

export type SignalsContext<STATE, ACT, PROPS> = [
  React.FC<{ children?: React.ReactNode; props?: Signal<PROPS> }>, // LocalContextProvider
  <T>(selector?: (input: STATE & ACT) => T) => T, // useLocalContextWithSelector
  () => STATE & ACT, // useLocalContext
  Context<STATE & ACT>, // LocalContext
];

export function createSignalsContext<
  STATE,
  ACT extends object,
  PROPS extends object,
>(
  state: (props?: PROPS) => STATE,
  hook: (state: STATE, props?: PROPS) => ACT,
): SignalsContext<STATE, ACT, PROPS> {
  const LocalContext = createContext<STATE & ACT>(null);

  const LocalContextProvider: React.FC<{
    children?: React.ReactNode;
    props?: Signal<PROPS>;
  }> = ({ children, props }) => {
    const stateRef = React.useRef<STATE>(state(props?.value));
    const hookRef = useRef<ACT>(hook?.(stateRef?.current, props?.value));
    const ctxValueRef = useRef<STATE & ACT>({
      ...stateRef?.current,
      ...hookRef?.current,
    });

    return (
      // @ts-ignore
      <LocalContext.Provider value={ctxValueRef.current}>
        {children}
      </LocalContext.Provider>
    );
  };

  function useLocalContextWithSelector<T>(
    selector?: (input: STATE & ACT) => T,
  ): T {
    const context = useContextSelector(LocalContext, selector);
    if (!context) {
      throw new Error(
        `useLocalContextWithSelector must be used within a LocalContextProvider`,
      );
    }
    return context;
  }

  const useLocalContext = (): STATE & ACT =>
    useLocalContextWithSelector((x) => x);

  return [
    LocalContextProvider,
    useLocalContextWithSelector,
    useLocalContext,
    LocalContext,
  ];
}
